import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { defineStore } from 'pinia';

import { useFormatLicensePlate } from '@/composables/useFormatLicensePlate.js';
import { usePrefillParams, useUrlParams } from '@/composables/useUrlParams.js';
import { aanvragerStore } from '@/stores/aanvrager';
import { aanvullendeDekkingenStore } from '@/stores/aanvullendedekkingen';
import { antwoordAcceptatieVragenStore } from '@/stores/antwoordacceptatievragen';
import { antwoordAkkoordVragenStore } from '@/stores/antwoordakkoordvragen';
import { antwoordSlotVragenStore } from '@/stores/antwoordslotvragen';
import { bestuurderStore } from '@/stores/bestuurder';
import { eigenaarStore } from '@/stores/eigenaar';
import { kentekenhouderStore } from '@/stores/kentekenhouder';
import { pakketStore } from '@/stores/pakket';
import { verzekeringStore } from '@/stores/verzekering';
import { voertuigStore } from '@/stores/voertuig.js';
import { vragenStore } from '@/stores/vragen';
import { woningStore } from '@/stores/woning.js';

export const formStore = defineStore({
  id: 'form',
  state: () => ({
    baseLoader: false,
    baseSpinner: false,
    error: false,

    tenant: 'default',
    // eslint-disable-next-line no-undef
    funnelType: funnelType ? funnelType : null,
    overruleFunnelType: null,
    funnelVersion: 'dev-local',
    funnelEntry: '',
    devMode: false,
    page: '', // wordt vanuit de router gevuld bij iedere route wijziging
    stepKey: '', // wordt vanuit de router gevuld bij iedere route wijziging
    sha: '',
    medewerker: '',
    alsGeenNeeBegonnen: '',
    updatevoogd: {},
    sortedRoutes: {},
    multistepRoutes: {},

    questions: {},

    specialist: {
      name: '',
      avatar: '',
    },
    ktv: {
      aantal_beoordelingen: null,
      percentage_aanbevelingen: null,
      rating: null,
    },
    toevoegingenOpties: [''],

    // StartCalculation
    voertuigMerken: null,
    voertuigModels: null,
    voertuigTypes: null,

    // Company Information
    companyOptions: [],

    // PersonalData
    radioClaimFree: null,
    totalInsuredYears: null,
    damageYears: null,
    bedrijfDetails: [],
    sbiOptions: [],

    // ChooseCoverage
    indicatiePremie: null,

    // Can request insurance
    canRequestInsurance: true,

    // Info labels
    aanvullendedekkingeninfo: {},
    faq: {},
    info: {},
    links: {},

    filters: {
      ownRisk: 'nvt',
      characteristics: [],
      price: [0, 500],
      sliderMin: 0,
      sliderMax: 500,
    },
    premies: [],
    premiumsDiffer: false,
    localKenteken: '',
    voertuigSoortOriginal: '',
    startedWithPrefills: true,
    skeletonLoader: false,
    analytics: false,
    affiliate: {
      code: '',
      tt_code: '',
      tt_campagne: '',
    },
    verklaringen: {}
  }),
  getters: {
    getFunnelVersion(state) {
      return state.funnelVersion;
    },
    getAanvullendeDekkingenInfo(state) {
      return state.aanvullendedekkingeninfo;
    },
    getSha(state) {
      return state.sha;
    },
    getFaq(state) {
      return state.faq;
    },
    getVoertuigBrands(state) {
      return state.voertuigMerken;
    },
    getVoertuigModels(state) {
      return state.voertuigModels;
    },
    getVoertuigTypes(state) {
      return state.voertuigTypes;
    },
    getIndicatiePremie(state) {
      return state.indicatiePremie;
    },
    getIsAanhangwagen(state) {     
      return state.funnelType === 'aanhangwagen';
    },
    skipStart() {
      const { adres } = bestuurderStore();
      const { kenteken, type_id } = voertuigStore();
      return adres !== '' && (type_id !== '' || kenteken !== '') && this.startedWithPrefills;
    },
  },
  actions: {
    removeRouteByName(name) {
      const index = this.sortedRoutes.indexOf(name);
      if (index > -1) {
        this.sortedRoutes.splice(index, 1);
      }
    },
    setStartedWithPrefills(value) {
      this.startedWithPrefills = value;
    },
    setSkeletonLoader(value) {
      this.skeletonLoader = value;
    },
    setKtv(value) {
      this.ktv = value;
    },
    setError(errorMessage) {
      this.error = true;
      this.setBaseSpinner(false);
      this.setBaseLoader(false);
      // Throw error zodat je niet alsnog naar de volgende pagina word gestuurd
      throw new Error(errorMessage);
    },
    setBaseSpinner(value) {
      this.baseSpinner = value;
    },
    setAlsGeenNeeBegonnen() {
      const pakket = pakketStore();
      if (this.alsGeenNeeBegonnen === '') {
        this.alsGeenNeeBegonnen = pakket.getGeennee;
      }
    },
    setUpdateVoogd(val) {
      const verzekering = verzekeringStore();
      let foundVoogd = false;

      for (const key of Object.keys(verzekering.verzekering)) {
        if (verzekering.verzekering[key].leverancier === 'Voogd') {
          foundVoogd = true;
          break;
        }
      }

      this.updatevoogd = foundVoogd ? val : false;
    },
    setPriceRange(value) {
      this.filters.price = value;
    },
    setBaseLoader(value) {
      this.baseLoader = value;
    },
    setOverruleFunnelType(value) {
      this.overruleFunnelType = value;
    },
    setFunnelType(value) {
      this.funnelType = value;
    },
    setFunnelVersion(value) {
      this.funnelVersion = value;
    },
    setFunnelEntry(value) {
      this.funnelEntry = value;
    },
    setTenant(value) {
      this.tenant = value;
    },
    hideLoaders() {
      this.baseLoader = false;
      this.baseSpinner = false;
    },
    getHasVoertuig() {
      const voertuigen = voertuigStore();
      return voertuigen.type_id !== '';
    },
    getHasAddress() {
      const bestuurder = bestuurderStore();
      return bestuurder.adres !== '';
    },
    getHasDateOfBirth() {
      const bestuurder = bestuurderStore();
      return bestuurder.geboortedatum !== '';
    },
    getHasVragenAantalSchadevrijeJaren() {
      const vragen = vragenStore();
      return vragen.voertuig.AantalSchadevrijeJaren !== '';
    },
    getHasVragenAantalKmPerJaar() {
      const vragen = vragenStore();
      return vragen.voertuig.AantalKmPerJaar !== '';
    },
    getHasVragenGebruik() {
      const vragen = vragenStore();
      return vragen.voertuig.Gebruik !== '';
    },
    getHasDekkingKeuze() {
      const verzekering = verzekeringStore();
      return verzekering.dekking !== null;
    },
    getHasVerzekeringKeuze() {
      const verzekering = verzekeringStore();
      const pakket = pakketStore();
      return verzekering.naam !== null || pakket.maatwerk;
    },
    getHasIngangsDatum() {
      const verzekering = verzekeringStore();
      return verzekering.ingangsdatum !== null;
    },
    getHasRekeningnummer() {
      const pakket = pakketStore();
      const aanvrager = aanvragerStore();
      return pakket.betalingstermijn === 'Jaar' || aanvrager.iban !== null;
    },
    getAppliciantForRequest() {
      const aanvrager = aanvragerStore();
      const bestuurder = bestuurderStore();
      if (aanvrager.is_bestuurder === '1') {
        // Als bestuurder ook de aanvrager is, dan bestuurder antwoorden samenvoegen met de aanvrager, dan pas opsturen.
        aanvrager.aantalschuldschaden = bestuurder.aantalschuldschaden;
        aanvrager.adres = bestuurder.adres;
        aanvrager.straat = bestuurder.straat;
        aanvrager.huisnummer = bestuurder.huisnummer;
        aanvrager.toevoeging = bestuurder.toevoeging;
        aanvrager.postcode = bestuurder.postcode;
        aanvrager.woonplaats = bestuurder.woonplaats;
        aanvrager.geboortedatum = bestuurder.geboortedatum;
      }

      return JSON.parse(JSON.stringify(aanvrager.$state));
    },
    getVragenForRequest() {
      const vragen = vragenStore();
      const verzekering = verzekeringStore();
      let filteredVragen = {};

      // Vragen toevoegen aan request op basis van gekozen verzekering
      Object.keys(verzekering.$state.verzekering).forEach((item) => {
        const lowerCaseItem = item.toLowerCase();
        if (vragen.$state[lowerCaseItem]) {
          filteredVragen[lowerCaseItem] = vragen.$state[lowerCaseItem];
        }
      });

      return filteredVragen;
    },
    setDriverIfAppliciantIsDriver() {
      const aanvrager = aanvragerStore();
      const bestuurder = bestuurderStore();

      if (aanvrager.is_bestuurder === '1') {
        bestuurder.adres = aanvrager.$state.adres;
        bestuurder.straat = aanvrager.$state.straat;
        bestuurder.huisnummer = aanvrager.$state.huisnummer;
        bestuurder.toevoeging = aanvrager.$state.toevoeging;
        bestuurder.postcode = aanvrager.$state.postcode;
        bestuurder.woonplaats = aanvrager.$state.woonplaats;
        bestuurder.land = aanvrager.$state.land;
        bestuurder.geboortedatum = aanvrager.$state.geboortedatum;
      }
    },
    doStateUpdate(response) {
      // Aanvrager (en bestuurder als deze identiek is aan de aanvrager).
      if (Object.keys(response).includes('aanvrager')) {
        const aanvrager = aanvragerStore();
        aanvrager.$patch(response.aanvrager);
        if (aanvrager.is_bestuurder === '1') {
          const bestuurder = bestuurderStore();
          bestuurder.$patch(response.aanvrager);
          bestuurder.is_aanvrager = 0;
          bestuurder.is_eigenaar = 0;
          bestuurder.is_kentekenhouder = 0;
        }
      }

      // Aanvullende dekkingen
      const aanvullendeDekkingen = aanvullendeDekkingenStore();
      if (Object.keys(response).includes('alleaanvullendedekkingen')) {
        aanvullendeDekkingen.setAlleAanvullendeDekkingen(response.alleaanvullendedekkingen);
      }
      if (Object.keys(response).includes('aanvullendedekkingen')) {
        aanvullendeDekkingen.setAanvullendeDekkingen(response.aanvullendedekkingen);
      }

      // Antwoord Acceptatievragen
      if (Object.keys(response).includes('antwoordacceptatievragen')) {
        const antwoordacceptatievragen = antwoordAcceptatieVragenStore();
        antwoordacceptatievragen.$state.acceptatieAntwoorden = response.antwoordacceptatievragen;
      }

      // Antwoord Akkoordvragen
      if (Object.keys(response).includes('antwoordAkkoordVragen')) {
        const antwoordAkkoordVragen = antwoordAkkoordVragenStore();
        antwoordAkkoordVragen.$state.akkoordAntwoorden = response.antwoordakkoordvragen;
      }

      // Antwoord Slotvragen
      if (Object.keys(response).includes('antwoordSlotVragen')) {
        const antwoordSlotVragen = antwoordSlotVragenStore();
        antwoordSlotVragen.$state.slotAntwoorden = response.antwoordslotvragen;
      }

      // Bestuurder
      if (Object.keys(response).includes('bestuurder')) {
        const bestuurder = bestuurderStore();
        bestuurder.$patch(response.bestuurder);
      }

      // Eigenaar
      if (Object.keys(response).includes('eigenaar')) {
        const eigenaar = eigenaarStore();
        eigenaar.$patch(response.eigenaar);
      }

      // SHA
      if (Object.keys(response).includes('sha')) {
        this.sha = response.sha;
      }

      // UPDATEVOOGD
      if (Object.keys(response).includes('updatevoogd')) {
        this.updatevoogd = response.updatevoogd;
      }

      // Tenant
      if (Object.keys(response).includes('tenant')) {
        this.tenant = response.tenant;
      }

      // Aanvullende dekkingen info
      if (Object.keys(response).includes('aanvullendedekkingeninfo')) {
        this.aanvullendedekkingeninfo = response.aanvullendedekkingeninfo;
      }

      // FAQ's
      if (Object.keys(response).includes('faq')) {
        this.faq = response.faq;
      }

      // Info
      if (Object.keys(response).includes('info')) {
        this.info = response.info;
      }

      // Links
      if (Object.keys(response).includes('links')) {
        this.links = response.links;
      }

      // Specialist
      if (Object.keys(response).includes('specialist')) {
        this.specialist = response.specialist;
      }

      // Kentekenhouder
      if (Object.keys(response).includes('kentekenhouder')) {
        const kentekenhouder = kentekenhouderStore();
        kentekenhouder.$patch(response.kentekenhouder);
      }

      // Pakket
      if (Object.keys(response).includes('pakket')) {
        const pakket = pakketStore();
        pakket.$patch(response.pakket);
      }

      // Verzekering
      if (Object.keys(response).includes('verzekering')) {
        const verzekering = verzekeringStore();
        verzekering.verzekering = response.verzekering;
      }

      // Voertuig
      if (Object.keys(response).includes('voertuigen')) {
        const voertuigResponse = Object.values(response.voertuigen);
        const voertuigen = voertuigStore();
        voertuigen.$patch(voertuigResponse[0]);

        // update the baseLicenceplate component here.
        const { formatLicensePlate } = useFormatLicensePlate();
        const licence = voertuigen.kenteken ?? '';
        this.localKenteken = formatLicensePlate(licence);
        this.voertuigSoortOriginal = this.funnelType;
      }

      // Vragen
      if (Object.keys(response).includes('vragen')) {
        const vragen = vragenStore();
        vragen.$patch(response.vragen);
      }

      // Can request insurance
      if (Object.keys(response).includes('alle_vragen_positief_beantwoord')) {
        this.canRequestInsurance = response.alle_vragen_positief_beantwoord;
      }
    },

    async initializeState() {
      const voertuig = voertuigStore();
      const pakket = pakketStore();
      const vragen = vragenStore();
      const { newSha, newMedewerker, isDev, provisieKey, vestigingKey, collectiviteitsCode, removeHistory, hasUrlParams } =
        useUrlParams();
      const { newKentekenOnbekend, hasPrefillParams } = usePrefillParams();

      // Check the parameters in the url for additional settings.
      removeHistory(['sha', 'vkey', 'ccode', 'prefill_kenteken_onbekend', 'p_key']);
      if (newSha) this.sha = newSha;
      if (newMedewerker) pakket.medewerker = newMedewerker;
      this.devMode = isDev === '1';
      if (newKentekenOnbekend) voertuig.kentekenOnbekend = newKentekenOnbekend === 'on';
      if (provisieKey) vragen.voertuig.ProvisieKey = provisieKey;
      if (collectiviteitsCode) vragen.insuretostudy.Collectiviteit = collectiviteitsCode;
      // <--

      return await axios
        .post(`/init`, {
          module: voertuig.soort,
          sha: this.sha,
          spa_versie: this.getFunnelVersion,
          vestiging_key: vestigingKey,
          url: window.location.href,
        })
        .then((response) => {
          if (response.data) {
            this.doStateUpdate(response.data);
            // I don't know why we have to check the urlParams and prefillParams here, but I don't want to break anything...
            if (voertuig.kenteken !== '' && hasUrlParams.value && hasPrefillParams.value) {
              voertuig.fetchVoertuigData(voertuig.kenteken).then((response) => {
                Object.assign(voertuig, JSON.parse(JSON.stringify(response)));
              });
            }
            this.setDriverIfAppliciantIsDriver();
            // Sentry
            Sentry.setExtra('sha', response.data.sha);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
          this.setBaseLoader(false);
        });
    },

    async fetchVoertuigMerken(soort, bouwdatum, brandstof) {
      this.setBaseSpinner(true);
      this.voertuigMerken = null;
      this.voertuigModels = null;
      this.voertuigTypes = null;

      return await axios
        .post('voertuig/merken', {
          voertuigsoort: soort,
          bouwdatum: bouwdatum,
          brandstof: brandstof,
        })
        .then((res) => {
          if (res.data.length > 0) {
            this.voertuigMerken = res.data.map(({ Naam: label, Id: value }) => ({ value, label }));
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchVoertuigModellen(soort, bouwdatum, brandstof, merk_id) {
      this.setBaseSpinner(true);
      this.voertuigModels = null;
      this.voertuigTypes = null;

      return await axios
        .post('voertuig/modellen', {
          voertuigsoort: soort,
          bouwdatum: bouwdatum,
          brandstof: brandstof,
          transmissie: null,
          merkid: merk_id,
        })
        .then((res) => {
          if (res.data.length > 0) {
            this.voertuigModels = res.data.map(({ Naam: label, Id: value }) => ({ value, label }));
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchVoertuigTypes(soort, bouwdatum, brandstof, merk_id, model_id) {
      this.setBaseSpinner(true);
      this.voertuigTypes = null;
      const voertuig = voertuigStore();
      return await axios
        .post('voertuig/types', {
          voertuigsoort: soort,
          bouwdatum: bouwdatum,
          brandstof: brandstof,
          transmissie: null,
          merkid: merk_id,
          modelid: model_id,
        })
        .then((res) => {
          if (res.data.length > 0) {
            this.voertuigTypes = res.data.map(({ Naam: label, Id: value }) => ({ value, label }));
          }
          voertuig.brandstof_soort = brandstof;
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchVoertuigTypeDetails(soort, bouwdatum, type_id) {
      this.setBaseSpinner(true);
      const form = formStore();
      const voertuig = voertuigStore();
      return await axios
        .post('voertuig/typedetails/new', {
          voertuigsoort: soort,
          bouwdatum: bouwdatum,
          typeid: type_id,
        })
        .then((response) => {
          if (response.data) {
            for (const [key, value] of Object.entries(response.data)) {
              voertuig[key] = value;
            }

            const pakket = pakketStore();

            if (
              form.voertuigSoortOriginal === response.data.berekende_funnel ||
              (form.voertuigSoortOriginal === 'auto' &&
                (response.data.berekende_funnel === 'personenauto' || response.data.berekende_funnel === 'bestelauto'))
            ) {
              // Set type module in voordeelpakket
              pakket.setModule(response.data.berekende_funnel);
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchVragen() {
      this.setBaseSpinner(true);

      return await axios
        .post('/get-all-vragen', {
          sha: this.sha,
          spa_versie: this.getFunnelVersion,
        })
        .then((res) => {
          if (res.data) {
            this.questions = res.data;

            // If there are no questions, remove the route from the sortedRoutes
            for (const key in this.questions) {
              const routeName = key
                .replace(/([a-z])([A-Z])/g, '$1-$2')
                .toLowerCase()
                .replace(/vragen/g, '-vragen');
              if (Array.isArray(this.questions[key]) && this.questions[key].length === 0) {
                this.removeRouteByName(routeName);
              }
            }
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },

    async postData(endpoint, data, successCallback, isAanvraag = false) {
      const aanvrager = aanvragerStore();
      const aanvullendeDekkingen = await aanvullendeDekkingenStore();
      const antwoordacceptatievragen = await antwoordAcceptatieVragenStore();
      const antwoordAkkoordVragen = await antwoordAkkoordVragenStore();
      const antwoordSlotVragen = await antwoordSlotVragenStore();
      const bestuurder = await bestuurderStore();
      const eigenaar = await eigenaarStore();
      const kentekenhouder = await kentekenhouderStore();
      const pakket = await pakketStore();
      const verzekering = await verzekeringStore();
      const voertuig = await voertuigStore();
      const woning = await woningStore();

      const requestData = {
        aanvullendedekkingen: JSON.parse(JSON.stringify(aanvullendeDekkingen.$state.alleaanvullendedekkingen)),
        antwoordacceptatievragen: JSON.parse(JSON.stringify(antwoordacceptatievragen.$state.acceptatieAntwoorden)),
        antwoordakkoordvragen: JSON.parse(JSON.stringify(antwoordAkkoordVragen.$state.akkoordAntwoorden)),
        antwoordslotvragen: JSON.parse(JSON.stringify(antwoordSlotVragen.$state.slotAntwoorden)),
        aanvrager: this.getAppliciantForRequest(),
        bestuurder: aanvrager.is_bestuurder === '1' ? null : JSON.parse(JSON.stringify(bestuurder.$state)),
        eigenaar: aanvrager.is_eigenaar === '0' ? JSON.parse(JSON.stringify(eigenaar.$state)) : null,
        page: this.page,
        pakket: JSON.parse(JSON.stringify(pakket.$state)),
        sha: this.sha,
        spa_versie: this.getFunnelVersion,
        updatevoogd: JSON.parse(JSON.stringify(this.$state.updatevoogd)),
        verzekering: JSON.parse(JSON.stringify(verzekering.$state.verzekering)),
        vragen: this.getVragenForRequest(), // todo: ook op basis van je keuze, alleen de vragen die bij de geselecteerde verzekeringen horen
        voertuig: Object.prototype.hasOwnProperty.call(verzekering.$state.verzekering, 'Voertuig')
          ? JSON.parse(JSON.stringify(voertuig.$state))
          : null,
        woning: this.funnelType === 'voordeelpakket' ? JSON.parse(JSON.stringify(woning.$state)) : null,
      };

      if (aanvrager.is_kentekenhouder === '0' && eigenaar.is_kentekenhouder !== '1') {
        requestData.kentekenhouder = JSON.parse(JSON.stringify(kentekenhouder.$state));
      }

      try {
        let response;
        if (!isAanvraag) {
          response = await axios.post(`${this.funnelType}/${endpoint}`, requestData);
        } else {
          response = await axios.post(`${endpoint}`, requestData);
        }
        successCallback(response.data);
        // If we have a successfull response, we can disable the error.
        this.error = false;
        return response.data;
      } catch (error) {
        this.setError(error);
      }
    },
    async doUpdate() {
      this.setBaseSpinner(true);
      await this.postData('updaten', {}, (data) => {
        this.doStateUpdate(data);
      });
    },
    async updatenIndicatiePremies() {
      this.setBaseSpinner(true);
      await this.postData('updaten/indicatiepremies', {}, (data) => {
        if (data) {
          this.doStateUpdate(data);
          this.indicatiePremie = data.indicatiePremie;
        }
      });
    },
    async updatenPremies() {
      this.setBaseLoader(true);
      await this.postData('updaten/premies', {}, (data) => {
        if (data) {
          this.doStateUpdate(data);
          this.premies = data.premies;

          if (this.premies && this.premies.length > 0) {
            this.filters.sliderMin = Math.floor(this.premies[0].totaalpremie);
            this.filters.sliderMax = Math.ceil(this.premies[this.premies.length - 1].totaalpremie);
            this.setPriceRange([this.filters.sliderMin, this.filters.sliderMax]);
          }
        }
      }).then((res) => {
        this.setBaseLoader(false);
        return res;
      });
    },
    async updatenAanvullendedekkingen() {
      this.setBaseSpinner(true);
      await this.postData('updaten/aanvullendedekkingen', {}, (data) => {
        if (data) {
          this.doStateUpdate(data);
        }
      });
    },
    async saveSessionForLater() {
      this.setBaseSpinner(true);
      await this.postData(
        'bewaarvoorlater',
        {},
        (data) => {
          this.doStateUpdate(data);
        },
        true,
      );

      this.setBaseSpinner(false);
    },
    async updatenSubmitPakket() {
      this.setBaseSpinner(true);
      const pakket = pakketStore();
      await this.postData('updaten/aanvragen', {}, (data) => {
        if (data) {
          this.doStateUpdate(data);
        }
        if (!pakket.maatwerk && data.errors && data.errors.length > 0) {
          this.setError(data.errors[0]);
        }
      });
    },
    async requestInsurance() {
      this.setBaseSpinner(true);

      await this.postData('afsluiten', {}, (data) => {
        this.doStateUpdate(data);
      });
    },
    async requestPayment() {
      this.setBaseSpinner(true);

      await this.postData('afsluiten', {}, (data) => {
        // betalen endpoint returns an URL. Redirect to this url
        if (data && data.redirectUrl) {
          window.location.href = data.redirectUrl;
        } else {
          console.error('Invalid URL returned from API.');
        }
      });
    },
    async fetchMaatschappijenLogos() {
      const pakket = pakketStore();
      return await axios
        .post(`/maatschappijen-logos`, {
          sha: this.sha,
          module: pakket.module,
          spa_versie: this.getFunnelVersion,
        })
        .then((res) => {
          return res.data;
        });
    },
    async fetchAddress(postcode, huisnummer, toevoeging) {
      const voertuig = voertuigStore();
      this.setBaseSpinner(true);

      return await axios
        .post('postcodecheck', {
          module: voertuig.soort,
          postcode: postcode,
          huisnummer: huisnummer,
          toevoeging: toevoeging,
        })
        .then((res) => {
          const response = res.data;
          if (res.data.message) {
            response.addressNotFound = true;
            this.toevoegingenOpties = [];
          } else {
            const hasHouseNumberAddition = response.houseNumberAdditions[1] !== '';
            if (hasHouseNumberAddition) {
              this.toevoegingenOpties = response.houseNumberAdditions;
            } else {
              this.toevoegingenOpties = [];
            }

            response.addressNotFound = response.houseNumberAddition !== toevoeging;
          }
          return response;
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchBedrijfInformatie(bedrijfSearch, bedrijfPlaatsnaam = null) {
      if (bedrijfSearch !== '') {
        return await axios
          .post('company-information/search', {
            searchTerm: bedrijfSearch,
            city: bedrijfPlaatsnaam,
          })
          .then((res) => {
            return res.data;
          })
          .catch(() => {})
          .finally(() => {});
      }
    },
    async fetchBedrijfInformatieByAddress(postalCode, houseNumber, houseNumberAddition = null) {
      this.setBaseSpinner(true);
      return await axios
        .post('company-information/address-search', {
          postalCode,
          houseNumber,
          houseNumberAddition,
        })
        .then((res) => {
          return res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchKvkDetails(kvkNumber) {
      this.setBaseSpinner(true);
      return await axios
        .post('company-information/details', {
          companyNumber: kvkNumber,
        })
        .then((res) => {
          return res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    async fetchBranchDetails(branchNumber) {
      this.setBaseSpinner(true);
      return await axios
        .post('company-information/branch', {
          branchNumber: branchNumber,
        })
        .then((res) => {
          return res.data;
        })
        .catch(() => {})
        .finally(() => {
          this.setBaseSpinner(false);
        });
    },
    openClausuleblad(premium) {
      this.setBaseSpinner(true);
      const pakket = pakketStore();
      axios
        .post(
          'clausuleblad',
          {
            kenmerk: premium.kenmerk,
            product: pakket.module === 'voordeelpakket' ? 'woonhuis' : pakket.module,
            productid: premium.productid,
            berekening_id: premium.berekening_id,
            sha: this.getSha,
            spa_versie: this.getFunnelVersion,
            versie: premium.versie,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/pdf',
              'Content-Disposition': 'filename="Clausuleblad Autoverzekering"',
            },
            responseType: 'blob',
          },
        )
        .then((response) => {
          const blob = new Blob([response.data], { type: 'application/pdf' });
          if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, 'clausuleblad-autoverzekering.pdf');
          } else {
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'clausuleblad-autoverzekering.pdf';
            link.click();
          }
          this.setBaseSpinner(false);
        })
        .catch(() => {
          this.setBaseSpinner(false);
        });
    },
    async requestQuote() {
      return await this.postData('aanvraag/offerte', {}, (data) => {
        if (data) {
          return data;
        }
      }, true);
    },
  },
  persist: true,
});
